import React from 'react';

const OutputsToolkit = () => {
  const scheduleComparison = [
    {
      focus: "Training Load",
      offSeason: "High (Skill Development & Foundation)",
      inSeason: "Moderate (Maintenance & Recovery)"
    },
    {
      focus: "Academic Load",
      offSeason: "Moderate (Foundation Building)",
      inSeason: "High (Peak Academic Performance)"
    },
    {
      focus: "Recovery Focus",
      offSeason: "Active Recovery & Conditioning",
      inSeason: "Strategic Rest & Injury Prevention"
    },
    {
      focus: "Mentoring",
      offSeason: "Intensive Personal Development",
      inSeason: "Performance Support & Guidance"
    },
    {
      focus: "Social-Emotional Learning",
      offSeason: "Deep Skill Building",
      inSeason: "Application & Reinforcement"
    },
    {
      focus: "Downtime",
      offSeason: "Structured Free Time",
      inSeason: "Essential Recovery Periods"
    }
  ];

  const trainingElements = [
    "Performance Journal",
    "Speed Training",
    "Sleep Study",
    "Meal Plan",
    "Rest/Recovery Menu",
    "Recruiting"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-spi-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-light text-white mb-6">
            Outputs and Toolkit
          </h1>
          <p className="text-xl text-spi-100 leading-relaxed">
            Your Central Hub for Research & Design
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-6">Welcome to Your Resource Hub</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Access everything you need to stay informed and organized throughout the R&D process: forms, schedules, 
              updates, and communication tools. All in one place!
            </p>
          </div>
        </div>
      </section>

      {/* Scheduling Philosophy */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-6">Scheduling Philosophy</h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
              A student's day at Sonom Performance Institute contrasts the off-season and in-season experiences. 
              The goal is to sustain excellence while protecting well-being.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-spi-200">
                    <th className="text-left py-4 px-4 text-lg font-semibold text-gray-900">Focus Area</th>
                    <th className="text-left py-4 px-4 text-lg font-semibold text-spi-600">Off-Season</th>
                    <th className="text-left py-4 px-4 text-lg font-semibold text-spi-700">In-Season</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleComparison.map((item, index) => (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="py-4 px-4 font-medium text-gray-900">{item.focus}</td>
                      <td className="py-4 px-4 text-gray-600">{item.offSeason}</td>
                      <td className="py-4 px-4 text-gray-600">{item.inSeason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sample Schedules Link */}
          <div className="text-center">
            <p className="text-gray-600 mb-4">Sample schedules available for detailed review</p>
            <a 
              href="https://docs.google.com/document/d/sample-schedules" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-spi-600 text-white hover:bg-spi-700 transition-colors rounded-lg font-medium"
            >
              Learn More - View Sample Schedules
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Main Content Sections */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-12">
          
          {/* Training */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Training</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              SPI provides elite strength and conditioning through Team Beast Mode. Industry professionals Justin King
              (L.I.G. Sports Group), Michael Susi (LinkedIn Director of Wellness), and Ron Johnson delivering resilience
              seminars, along with Laura Bixler (Penn State Health), support the program. Our aim is to unlock your full
              athletic potential with proven expertise and personalized programs.
            </p>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Training Elements</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {trainingElements.map((element, index) => (
                <div key={index} className="flex items-center p-3 bg-spi-50 rounded-lg">
                  <svg className="w-5 h-5 text-spi-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 font-medium">{element}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Academic Support */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Academic Support</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              Participating cyber charter schools provide all resources and staff. During the R&D, SPI will explore 
              appropriate academic support services designed to help students excel in their studies. Our goal is 
              to ensure every student reaches their full potential.
            </p>
            <a 
              href="https://docs.google.com/forms/d/weekly-checkin" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-spi-600 hover:text-spi-700 font-medium"
            >
              Weekly Check-in Form
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          {/* 2nd Step */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">2nd Step</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              The R&D phase of SPI prioritizes social-emotional learning for athletes. Student well-being requires 
              a sense of belonging. Our goal through 2nd Step is to support holistic development beyond physical training.
            </p>
            <a 
              href="https://www.youtube.com/watch?v=secondstep-spi" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-spi-600 hover:text-spi-700 font-medium"
            >
              Learn More - Watch Video
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h6m2-7a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </a>
          </div>

          {/* NIL Curriculum */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">NIL Curriculum</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              A practical NIL curriculum empowers athletes and their families to prepare for the changing landscape 
              of the NIL. Experience NIL readiness from the perspectives of players, journalism, sports marketing, 
              and beyond. Understanding the NIL has major career pathway implications that SPI will explore.
            </p>
            <a 
              href="https://docs.google.com/document/d/nil-curriculum" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-spi-600 hover:text-spi-700 font-medium"
            >
              Access Curriculum
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          {/* Transportation */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Transportation</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              SPI uses rideshare solutions and explains that 'Uber for Teens' offers safe, concierge transportation. 
              Based on the SPI Scheduling Philosophy, customizable transportation is paramount.
            </p>
            <a 
              href="https://help.uber.com/riders/article/uber-for-teens?nodeId=85c399ab-b302-4cbe-a97b-8196e2f4e5fc" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-spi-600 hover:text-spi-700 font-medium"
            >
              Learn More - Uber for Teens
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-spi-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-spi-100 mb-8 leading-relaxed">
            Access all resources and stay connected throughout your SPI journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/research-2026" 
              className="px-8 py-4 bg-white text-spi-700 hover:bg-gray-100 transition-colors rounded-lg font-medium text-lg shadow-lg hover:shadow-xl"
            >
              Explore R&D Program
            </a>
            <a 
              href="mailto:sonom.advantage@gmail.com" 
              className="px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-spi-700 transition-colors rounded-lg font-medium text-lg shadow-lg hover:shadow-xl"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OutputsToolkit;